import React, { useCallback, useEffect, useMemo, useState } from "react";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import { useDataLayer } from "../../../../Contexts/DataLayer.context.jsx";
import { getMyScouts, getUserFromToken } from "../../../../utils/functions.ts";
import "./tasks.style.scss";

const TASKS_ENDPOINT = "https://www.fbxchange.com/ReactPHP/tasks.php";
const BOX_WIDTH = 980;
const DEFAULT_POS = { x: typeof window !== "undefined" ? Math.max(20, window.innerWidth - BOX_WIDTH - 18) : 160, y: 92 };

const EMPTY_FORM = {
  title: "",
  description: "",
  assignedToScoutId: "",
  priority: "Normal",
  dueDate: "",
};

const STATUS_OPTIONS = ["Not Started", "In Progress", "Waiting", "Done"];
const PRIORITY_OPTIONS = ["Low", "Normal", "High"];

const canAssignTasks = (user) => {
  return String(user?.assignTask || "").trim().toLowerCase() === "yes";
};

const getScoutName = (user) => (
  user?.displayName ||
  user?.scoutName ||
  user?.name ||
  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
  String(user?.scout || "")
);

const normalizeScouts = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({
      userId: String(row?.userId ?? row?.scout ?? row?.id ?? "").trim(),
      displayName: String(row?.displayName ?? row?.name ?? row?.userId ?? "").trim(),
    }))
    .filter((row) => row.userId)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

const formatDate = (value) => {
  if (!value || value === "0000-00-00" || value === "0000-00-00 00:00:00") return "-";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value).split(" ")[0];
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (value) => {
  if (!value || value === "0000-00-00 00:00:00") return "-";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getDueSortValue = (value) => {
  if (!value || value === "0000-00-00" || value === "0000-00-00 00:00:00") return Number.MAX_SAFE_INTEGER;
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
};

const TaskManager = () => {
  const { scoutArray } = useDataLayer();
  const [user, setUser] = useState(null);
  const [scouts, setScouts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const canCreateTasks = canAssignTasks(user);
  const currentScoutId = String(user?.scout ?? user?.userId ?? "");
  const openTasks = useMemo(() => tasks.filter((task) => task.status !== "Done").length, [tasks]);
  const doneTasks = useMemo(() => tasks.filter((task) => task.status === "Done").length, [tasks]);
  const sortedTasks = useMemo(() => (
    [...tasks].sort((a, b) => {
      const dueDiff = getDueSortValue(a.dueDate) - getDueSortValue(b.dueDate);
      if (dueDiff !== 0) return dueDiff;
      return Number(a.taskId || 0) - Number(b.taskId || 0);
    })
  ), [tasks]);

  const apiPost = useCallback(async (payload) => {
    const activeUser = user || await getUserFromToken();
    if (!activeUser?.team || !activeUser?.scout) {
      throw new Error("Unable to read signed in user.");
    }

    const response = await fetch(TASKS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        team: activeUser.team,
        scoutId: activeUser.scout,
        scoutName: getScoutName(activeUser),
        userContactLevel: activeUser.userContactLevel ?? activeUser.security ?? "",
      }),
    });

    const data = await response.json();
    if (!data?.success) {
      throw new Error(data?.message || "Task request failed.");
    }

    return data;
  }, [user]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      const activeUser = await getUserFromToken();
      setUser(activeUser);

      if (!activeUser?.team || !activeUser?.scout) {
        setMessage("Unable to read signed in user.");
        setTasks([]);
        return;
      }

      let nextScouts = normalizeScouts(scoutArray);
      if (canAssignTasks(activeUser) && nextScouts.length === 0) {
        nextScouts = normalizeScouts(await getMyScouts(activeUser.scout, activeUser.team));
      }
      setScouts(nextScouts);

      const response = await fetch(TASKS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getTasks",
          team: activeUser.team,
          scoutId: activeUser.scout,
          scoutName: getScoutName(activeUser),
          userContactLevel: activeUser.userContactLevel ?? activeUser.security ?? "",
        }),
      });

      const data = await response.json();
      if (data?.success) {
        setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      } else {
        setTasks([]);
        setMessage(data?.message || "Unable to load tasks.");
      }
    } catch (error) {
      setTasks([]);
      setMessage(error?.message || "Unable to load tasks.");
    } finally {
      setLoading(false);
    }
  }, [scoutArray]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const createTask = async () => {
    if (!canCreateTasks) return;

    const title = form.title.trim();
    if (!title) {
      setMessage("Task title is required.");
      return;
    }
    if (!form.assignedToScoutId) {
      setMessage("Choose who this task is assigned to.");
      return;
    }

    const assignee = scouts.find((row) => String(row.userId) === String(form.assignedToScoutId));

    setSaving(true);
    setMessage("");
    try {
      const data = await apiPost({
        scriptName: "createTask",
        title,
        description: form.description.trim(),
        assignedToScoutId: form.assignedToScoutId,
        assignedToScoutName: assignee?.displayName || form.assignedToScoutId,
        priority: form.priority,
        dueDate: form.dueDate,
      });

      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      setForm(EMPTY_FORM);
      setCreateOpen(false);
      setMessage("Task saved.");
    } catch (error) {
      setMessage(error?.message || "Unable to add task.");
    } finally {
      setSaving(false);
    }
  };

  const updateTask = async (taskId, patch) => {
    setSaving(true);
    setMessage("");

    try {
      const data = await apiPost({
        scriptName: "updateTask",
        taskId,
        ...patch,
      });

      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      setMessage("Task updated.");
    } catch (error) {
      setMessage(error?.message || "Unable to update task.");
    } finally {
      setSaving(false);
    }
  };

  const updateTaskProgress = (task, value) => {
    const progress = Number(value || 0);
    const nextPatch = { progress };
    if (String(task?.status || "").trim() === "Not Started" && progress > 0) {
      nextPatch.status = "In Progress";
    }
    updateTask(task.taskId, nextPatch);
  };

  const updateNoteDraft = (taskId, value) => {
    setNoteDrafts((prev) => ({ ...prev, [taskId]: value }));
  };

  const addTaskNote = async (taskId) => {
    const noteText = String(noteDrafts[taskId] || "").trim();
    if (!noteText) {
      setMessage("Note is required.");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const data = await apiPost({
        scriptName: "addTaskNote",
        taskId,
        noteText,
      });

      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      setNoteDrafts((prev) => ({ ...prev, [taskId]: "" }));
      setMessage("Note added.");
    } catch (error) {
      setMessage(error?.message || "Unable to add note.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTask = async (task) => {
    if (!canCreateTasks) return;
    const taskName = String(task?.title || "this task").trim();
    const ok = window.confirm(`Are you sure you want to remove ${taskName}?`);
    if (!ok) return;

    setSaving(true);
    setMessage("");

    try {
      const data = await apiPost({
        scriptName: "deleteTask",
        taskId: task.taskId,
      });

      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      setMessage("Task deleted.");
    } catch (error) {
      setMessage(error?.message || "Unable to delete task.");
    } finally {
      setSaving(false);
    }
  };

  const archiveTask = async (taskId) => {
    if (!canCreateTasks) return;
    setSaving(true);
    setMessage("");

    try {
      const data = await apiPost({
        scriptName: "archiveTask",
        taskId,
      });

      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      setMessage("Task archived.");
    } catch (error) {
      setMessage(error?.message || "Unable to archive task.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DragBox2026
      storageKey="TASK_MANAGER_BOX_LOCATION"
      defaultPos={DEFAULT_POS}
      title="Tasks"
      countText={`${openTasks} Open | ${doneTasks} Done`}
      minWidth={BOX_WIDTH}
      maxWidth={BOX_WIDTH}
      zIndex={530}
      showReset
      wideTitle
    >
      <div className="task-manager-box">
        {canCreateTasks && (
          <>
            <div className="task-manager-add-row">
              <button type="button" onClick={() => setCreateOpen(true)} disabled={saving || createOpen}>
                Add
              </button>
            </div>
            {createOpen && (
              <div className="task-manager-create">
                <div className="task-manager-create-row">
                  <input
                    type="text"
                    value={form.title}
                    onChange={(event) => updateForm("title", event.target.value)}
                    placeholder="Task name"
                    className="task-manager-title-input"
                    disabled={saving}
                  />
                  <select
                    value={form.assignedToScoutId}
                    onChange={(event) => updateForm("assignedToScoutId", event.target.value)}
                    disabled={saving}
                  >
                    <option value="">Assign to</option>
                    {scouts.map((scout) => (
                      <option key={scout.userId} value={scout.userId}>
                        {scout.displayName || scout.userId}
                      </option>
                    ))}
                  </select>
                  <select value={form.priority} onChange={(event) => updateForm("priority", event.target.value)} disabled={saving}>
                    {PRIORITY_OPTIONS.map((priority) => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={form.dueDate}
                    onChange={(event) => updateForm("dueDate", event.target.value)}
                    disabled={saving}
                  />
                  <button type="button" className="task-manager-cancel" onClick={() => { setCreateOpen(false); setForm(EMPTY_FORM); }} disabled={saving}>
                    Cancel
                  </button>
                  <button type="button" className="task-manager-save" onClick={createTask} disabled={saving}>
                    Save
                  </button>
                </div>
                <textarea
                  value={form.description}
                  onChange={(event) => updateForm("description", event.target.value)}
                  placeholder="Notes"
                  disabled={saving}
                />
              </div>
            )}
          </>
        )}

        <div className="task-manager-toolbar">
          <div>{canCreateTasks ? "Team tasks" : "My tasks"}</div>
          <button type="button" onClick={loadTasks} disabled={loading || saving}>Refresh</button>
        </div>

        {message && <div className="task-manager-message">{message}</div>}
        {loading && <div className="task-manager-empty">Loading tasks...</div>}
        {!loading && tasks.length === 0 && <div className="task-manager-empty">No tasks assigned.</div>}

        {!loading && tasks.length > 0 && (
          <div className="task-manager-list">
            {sortedTasks.map((task) => {
              const isExpanded = String(expandedTaskId || "") === String(task.taskId);
              return (
                <div key={task.taskId} className="task-manager-task-wrap">
                  <div
                    className={`task-manager-task task-priority-${String(task.priority || "Normal").toLowerCase()} ${task.status === "Done" ? "task-manager-task-done" : ""} ${isExpanded ? "task-manager-task-expanded" : ""}`}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedTaskId(isExpanded ? null : task.taskId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setExpandedTaskId(isExpanded ? null : task.taskId);
                      }
                    }}
                  >
                    <div className="task-manager-task-title" title={task.description || task.title}>
                      <span className="task-manager-expand-indicator">{isExpanded ? "-" : "+"}</span>
                      {task.title}
                    </div>
                    <div className="task-manager-meta">
                      <span>{task.assignedToScoutName || task.assignedToScoutId}</span>
                      <span>Due {formatDate(task.dueDate)}</span>
                      <span>{task.priority || "Normal"}</span>
                    </div>
                    <div className="task-manager-status-text">{task.status || "Not Started"}</div>
                    <div className="task-manager-progress task-manager-progress-readonly">
                      <div className="task-manager-progress-bar" aria-label={`Progress ${Number(task.progress || 0)}%`}>
                        <span style={{ width: `${Number(task.progress || 0)}%` }} />
                      </div>
                      <span>{Number(task.progress || 0)}%</span>
                    </div>
                    {task.status === "Done" && canCreateTasks ? (
                      <button type="button" className="task-manager-archive" onClick={(event) => { event.stopPropagation(); archiveTask(task.taskId); }} disabled={saving}>
                        Archive
                      </button>
                    ) : canCreateTasks ? (
                      <button type="button" className="task-manager-delete" onClick={(event) => { event.stopPropagation(); deleteTask(task); }} disabled={saving}>
                        Delete
                      </button>
                    ) : (
                      <div className="task-manager-row-spacer" />
                    )}
                  </div>

                  {isExpanded && (
                    <div className="task-manager-detail">
                      <div className="task-manager-description-box">
                        <div className="task-manager-description-label">Task</div>
                        <div className="task-manager-detail-text">{task.description || "No notes entered for this task."}</div>
                      </div>
                      <div className="task-manager-detail-meta">
                        <span>Assigned: {task.assignedToScoutName || task.assignedToScoutId}</span>
                        <span>Due: {formatDate(task.dueDate)}</span>
                        <span>Updated: {task.updatedByScoutName || task.createdByScoutName || "-"} {formatDateTime(task.updatedAt)}</span>
                      </div>
                      {task.canEdit && (
                        <div className="task-manager-detail-controls">
                          <select
                            value={task.status || "Not Started"}
                            onChange={(event) => updateTask(task.taskId, { status: event.target.value })}
                            disabled={saving}
                            aria-label={`Status for ${task.title}`}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                          <div className="task-manager-progress">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="5"
                              value={Number(task.progress || 0)}
                              onChange={(event) => updateTaskProgress(task, event.target.value)}
                              disabled={saving}
                              aria-label={`Progress for ${task.title}`}
                            />
                            <span>{Number(task.progress || 0)}%</span>
                          </div>
                        </div>
                      )}
                      <div className="task-manager-notes">
                        <div className="task-manager-notes-title">Discussion</div>
                        <div className="task-manager-note-list">
                          {Array.isArray(task.notes) && task.notes.length > 0 ? (
                            task.notes.map((note) => {
                              const isOwnNote = String(note.createdByScoutId || "") === currentScoutId;
                              return (
                                <div key={note.noteId} className={`task-manager-note ${isOwnNote ? "task-manager-note-own" : ""}`}>
                                  <div className="task-manager-note-meta">
                                    <span>{note.createdByScoutName || note.createdByScoutId || "-"}</span>
                                    <span>{formatDateTime(note.createdAt)}</span>
                                  </div>
                                  <div className="task-manager-note-text">{note.noteText}</div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="task-manager-note-empty">No discussion yet.</div>
                          )}
                        </div>
                        {task.canDiscuss && (
                          <form
                            className="task-manager-note-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              addTaskNote(task.taskId);
                            }}
                          >
                            <input
                              type="text"
                              value={noteDrafts[task.taskId] || ""}
                              onChange={(event) => updateNoteDraft(task.taskId, event.target.value)}
                              placeholder="Add a note"
                              maxLength={1000}
                              disabled={saving}
                            />
                            <button type="submit" disabled={saving || !String(noteDrafts[task.taskId] || "").trim()}>
                              Send
                            </button>
                          </form>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DragBox2026>
  );
};

export default TaskManager;
